import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTable, usePagination, useSortBy } from 'react-table'
import { useNavigate, useSearchParams } from 'react-router-dom'
import moment from 'moment'

import useOutsideClickListener from 'LegacyUtils/hooks/useOutsideClickListener'
import { getParams } from 'LegacyUtils/params'

import { fetchAnswers, deleteAnswer } from 'Actions/answers'
import { getAllAnswers, getAnswersTotalCount } from 'Selectors/answers'
import { useBlockLayout } from 'react-table/src/plugin-hooks/useBlockLayout'

import answersLogo from 'Assets/images/empty-answers.svg?url'
import searchIcon from 'Assets/images/search.svg?url'
import caredFilledDown from 'Assets/images/caret-filled-down.svg?url'
import caredFilledUp from 'Assets/images/caret-filled-up.svg?url'

const Options = ({ id }) => {
  const [show, setShow] = useState(false)
  const ref = useRef()

  const dispatch = useDispatch()
  const closeHandler = useCallback(() => setShow(false), [setShow])

  useOutsideClickListener(ref, closeHandler, show)

  const onDeleteClick = useCallback(
    (e) => {
      e.preventDefault()
      const canDelete = confirm('Are you sure you want to delete this answer? This cannot be undone.')
      if (!canDelete) return
      dispatch(deleteAnswer({ answerId: id }))
    },
    [dispatch, id]
  )

  const defaultClick = useCallback(
    (e) => {
      e.preventDefault()
      setShow(!show)
    },
    [show]
  )

  return (
    <div className='options-dots' onClick={defaultClick}>
      {show && (
        <div className='dropdown-menu' ref={ref}>
          <span className='dropdown-menu-arrow' />
          <a className='dropdown-item' onClick={onDeleteClick}>
            Delete
          </a>
        </div>
      )}
    </div>
  )
}

const AnswersList = () => {
  const navigate = useNavigate()
  const [, setSearchParams] = useSearchParams()
  const dispatch = useDispatch()

  const answers = useSelector(getAllAnswers)
  const totalAnswersCount = useSelector(getAnswersTotalCount)
  const params = getParams(null)

  const [pageCount, setPageCount] = useState(0)
  const [searchText, setSearchText] = useState('')
  const firstMount = useRef(false)

  const columns = useMemo(() => {
    return [
      {
        Header: 'NAME',
        accessor: 'name',
        disableSortBy: true
      },
      {
        Header: 'CREATED BY',
        accessor: 'user.name',
        disableSortBy: true
      },
      {
        id: 'created_at',
        Header: 'CREATED',
        accessor: (row) => row,
        Cell: ({ cell: { value } }) => {
          const createdAt = value.created_at
          return moment(createdAt).fromNow()
        },
        disableSortBy: false
      },
      {
        id: 'updated_at',
        Header: 'LAST UPDATED',
        accessor: (row) => row,
        Cell: ({ cell: { value } }) => {
          const { updated_at: updatedAt } = value
          return updatedAt ? moment(updatedAt).fromNow() : '-'
        },
        disableSortBy: false
      }
    ]
  }, [])

  const initialSortBy = useMemo(() => {
    const urlSortBy = params.get('sort_by') || 'created_at'
    const urlOrderBy = params.get('order_by') || 'desc'
    return [{ id: urlSortBy, desc: urlOrderBy === 'desc' }]
  }, [params])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    gotoPage,
    state: { pageIndex, pageSize, sortBy }
  } = useTable(
    {
      columns,
      data: answers,
      initialState: {
        pageIndex: params.get('page') ? parseInt(params.get('page')) - 1 : 0,
        pageSize: 15,
        sortBy: initialSortBy
      },
      disableSortRemove: true,
      manualPagination: true,
      pageCount,
      manualSortBy: true
    },
    useSortBy,
    usePagination,
    useBlockLayout
  )

  const currentPage = pageIndex + 1

  useEffect(() => {
    const offset = pageIndex * pageSize
    const limit = pageSize

    let sort_by = ''
    let order_by = ''

    sortBy.forEach((sort) => {
      sort_by = sort.id
      order_by = sort.desc ? 'desc' : 'asc'
    })

    const showToast = firstMount.current
    firstMount.current = true

    dispatch(fetchAnswers({ offset, limit, search: searchText, sort_by, order_by, showToast }))
  }, [dispatch, pageIndex, pageSize, searchText, sortBy])

  useEffect(() => {
    setPageCount(Math.ceil(totalAnswersCount / pageSize))
  }, [answers, totalAnswersCount, pageSize])

  useEffect(() => {
    const newParams = {
      page: currentPage
    }

    sortBy.forEach((sort) => {
      newParams.sort_by = sort.id
      newParams.order_by = sort.desc ? 'desc' : 'asc'
    })

    setSearchParams(newParams, { replace: true })
  }, [setSearchParams, currentPage, sortBy])

  const onSearchAnswers = (event) => {
    if (event.key !== 'Enter') {
      return
    }

    const { value } = event.target
    gotoPage(0)
    setSearchText(value)
  }

  return (
    <div className='answers-container'>
      <div className='page-top-bar'>
        <div className='title'>Answers</div>
        {rows.length > 0 && (
          <div className='right'>
            <fieldset className='search-container'>
              <img src={searchIcon} className='search-icon' alt='' />
              <input
                className='search-input'
                type='text'
                onKeyDown={onSearchAnswers}
                placeholder='Type and hit enter to search'
              />
            </fieldset>
            <button className='button-primary mr-3' onClick={() => navigate('create')}>
              New Answer
            </button>
            <div className='pagination'>
              <button
                className='previous-button pagination-button mr-1'
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              >
                ‹
              </button>{' '}
              <button className='next-button pagination-button' onClick={() => nextPage()} disabled={!canNextPage}>
                ›
              </button>{' '}
            </div>
          </div>
        )}
      </div>
      {rows.length > 0 && (
        <div>
          <div className='list-container'>
            <div {...getTableProps()} className='table'>
              <div className='thead'>
                {headerGroups.map((headerGroup) => {
                  const { key } = headerGroup.getHeaderGroupProps()
                  return (
                    <div className='tr' key={key} {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => {
                        const { key } = column.getHeaderProps()
                        return (
                          <div className='th' {...column.getHeaderProps(column.getSortByToggleProps())} key={key}>
                            {column.render('Header')}

                            {column.isSorted && (
                              <img
                                src={column.isSortedDesc ? caredFilledDown : caredFilledUp}
                                className='sort-indicator'
                                alt=''
                              />
                            )}
                          </div>
                        )
                      })}
                      <div className='th options-column' />
                    </div>
                  )
                })}
              </div>
              <div className='tbody' {...getTableBodyProps()}>
                {rows.map((row) => {
                  prepareRow(row)
                  const { key, ...rowProps } = row.getRowProps()
                  const id = row.original.id

                  return (
                    <div className='pointer tr' {...rowProps} key={key}>
                      {row.cells.map((cell) => {
                        const { key, ...cellProps } = cell.getCellProps()
                        return (
                          <div className='td' {...cellProps} key={key} onClick={() => navigate(`edit/${id}`)}>
                            {cell.render('Cell')}
                          </div>
                        )
                      })}
                      <div className='options td'>
                        <Options id={id} />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      {!rows.length && (
        <div className='empty-state'>
          <div className='empty-state-content'>
            <img className='mb-3' src={answersLogo} alt='' />
            <h1>Write answers for commonly asked questions</h1>
            <p>
              Answers let you create and manage all your simple Q&As in one place <br /> using rich text and images
            </p>
            <button onClick={() => navigate('create')} className='button-primary mb-3'>
              Create a new Answer
            </button>
            <br />
            {/* <a href=''>Learn more about answers</a> */}
          </div>
        </div>
      )}
    </div>
  )
}

export default AnswersList
